import { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { setNavigate } from '@/services/api/apiClient';
import { useTenantStore } from '@/store/tenantStore';
import { SeoHead } from '@/components/seo/SeoHead';
import { ROUTES } from './routes';
import { ProtectedRoute } from './ProtectedRoute';
import { AccountGuard } from './AccountGuard';
import { DocumentTitle } from './DocumentTitle';

import { HomePage } from '@/features/home/HomePage';
import { LoginPage } from '@/features/auth/LoginPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/features/auth/VerifyEmailPage';
import { EmailVerifiedPage } from '@/features/auth/EmailVerifiedPage';
import { MfaPage } from '@/features/auth/MfaPage';
import { DuplicateAccountPage } from '@/features/auth/DuplicateAccountPage';
import { MaintenancePage } from '@/features/maintenance/MaintenancePage';
import { JobsPage } from '@/features/jobs/JobsPage';
import { JobDetailPage } from '@/features/jobs/JobDetailPage';
import { SavedJobsPage } from '@/features/jobs/SavedJobsPage';
import { ViewJobsPage } from '@/features/jobs/ViewJobsPage';
import { ExtractedResumePage } from '@/features/jobs/ExtractedResumePage';
import { ApplicationFormPage } from '@/features/application/ApplicationFormPage';
import { EasyApplyPage } from '@/features/application/EasyApplyPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { EditProfilePage } from '@/features/profile/EditProfilePage';
import { AboutUsPage } from '@/features/aboutUs/AboutUsPage';
import { JobDescriptionDemoPage } from '@/features/demo/JobDescriptionDemoPage';
import { JobApplicationDemoPage } from '@/features/demo/JobApplicationDemoPage';
import { JOB_DESCRIPTION_1, JOB_DESCRIPTION_2 } from '@/features/demo/jobDescriptionDemoData';
import { onboardingRoutes } from '@/features/onboarding/entry';

/**
 * AppRouter — full route tree.
 * Mirrors Flutter routing_config.dart which splits routes into:
 *   commonNavigation (public routes)
 *   authorizedNavigation (auth-required routes)
 *
 * screen_view is not logged per route change: Flutter logs it only from
 * NavigationService.navigateTo(route, analyticsClass), so each navigation site calls
 * analyticsService.logScreenView itself.
 */

/**
 * Helper component to wire up the navigate function for the API interceptor.
 */
function NavigateInjector() {
  const navigate = useNavigate();
  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);
  return null;
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <NavigateInjectorWrapper />,
    children: [
      // ── Public routes (commonNavigation) ──
      { index: true, element: <Navigate to={ROUTES.HOME} replace /> },
      {
        path: 'home',
        element: (
          <>
            {/* Flutter WebCareerPage: Seo.head(tags: [MetaTag(name: 'title', content: 'Jobs')]) */}
            <SeoHead meta={[{ name: 'title', content: 'Jobs' }]} />
            <HomePage />
          </>
        ),
      },
      { path: 'login', element: <LoginPage /> },
      { path: 'signUp', element: <SignUpPage /> },
      { path: 'resetPassword', element: <ResetPasswordPage /> },
      { path: 'verifyEmail', element: <VerifyEmailPage /> },
      { path: 'emailVerified', element: <EmailVerifiedPage /> },
      { path: 'duplicateAccount', element: <DuplicateAccountPage /> },
      // Flutter WebAboutUsPage: header and footer only
      { path: 'aboutUs', element: <AboutUsPage /> },

      // Flutter WebJobDescriptionPage1 / 2: static demo jobs with their own SEO tags
      { path: '1', element: <JobDescriptionDemoPage job={JOB_DESCRIPTION_1} /> },
      { path: '2', element: <JobDescriptionDemoPage job={JOB_DESCRIPTION_2} /> },

      // Flutter WebJobApplication: prototype form, public (commonNavigation) and not gated by the page
      { path: 'jobApplication', element: <JobApplicationDemoPage /> },

      // Jobs listing + Job details — public in Flutter (commonNavigation)
      { path: 'jobs', element: <JobsPage /> },
      { path: 'jobs/:id', element: <JobDetailPage /> },

      // Saved jobs — public in Flutter (commonNavigation); signed out it shows "no records"
      { path: 'savedJobs', element: <SavedJobsPage /> },

      // Search by resume — public in Flutter (anonymous resume upload from Home)
      { path: 'search_by_resume', element: <ExtractedResumePage /> },

      // ── Auth-protected routes (authorizedNavigation) ──
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'candidateApplicationForm', element: <ApplicationFormPage /> },
          { path: 'profile', element: <ProfilePage /> },
          { path: 'application', element: <ApplicationFormPage /> },
          { path: 'application/:id', element: <ApplicationFormPage /> },
          { path: 'easyApplyApplication', element: <EasyApplyPage /> },
          { path: 'easyApplyApplication/:id', element: <EasyApplyPage /> },
          { path: 'editProfile', element: <EditProfilePage /> },
          { path: 'viewJobs', element: <ViewJobsPage /> },
          { path: 'viewJobs/_applied', element: <ViewJobsPage /> },
          { path: 'viewJobs/_invited', element: <ViewJobsPage /> },
          { path: 'viewJobs/_recommended', element: <ViewJobsPage /> },
          { path: 'viewJobs/_saved', element: <ViewJobsPage /> },
          // Legacy non-underscore paths from earlier React builds
          { path: 'viewJobs/applied', element: <Navigate to={ROUTES.VIEW_JOBS_APPLIED} replace /> },
          { path: 'viewJobs/invited', element: <Navigate to={ROUTES.VIEW_JOBS_INVITED} replace /> },
          { path: 'viewJobs/recommended', element: <Navigate to={ROUTES.VIEW_JOBS_RECOMMENDED} replace /> },
          { path: 'viewJobs/saved', element: <Navigate to={ROUTES.VIEW_JOBS_SAVED} replace /> },
          { path: 'mfa', element: <MfaPage /> },
          // Candidate onboarding: lazy-loaded, and only registered while the feature is enabled (not in prod)
          ...onboardingRoutes(),
        ],
      },

      // ── Catch-all: redirect unknown → /home ──
      { path: '*', element: <Navigate to={ROUTES.HOME} replace /> },
    ],
  },
]);

/**
 * Wrapper that injects NavigateInjector inside the RouterProvider context.
 * Child routes render through AccountGuard, which applies to every route (as in Flutter).
 *
 * Maintenance mode is checked first, as in Flutter commonNavigation(): while
 * content.isMaintainancePageVisible is set, every URL renders MaintenancePage in place.
 * DocumentTitle sets the tab title for every route.
 */
function NavigateInjectorWrapper() {
  const { isMaintenanceMode } = useTenantStore();
  return (
    <>
      <NavigateInjector />
      <DocumentTitle isMaintenanceMode={isMaintenanceMode} />
      {isMaintenanceMode ? <MaintenancePage /> : <AccountGuard />}
    </>
  );
}

export function AppRouter() {
  return <RouterProvider router={router} />;
}
